import { useEffect, useRef } from "preact/hooks";

type UseFocusTrapOptions = {
  open: boolean;
  onEscape?: () => void;
};

/**
 * A hook that implements focus trapping for modal dialogs.
 * - Traps Tab/Shift+Tab within the container
 * - Auto-focuses the first focusable element when opened
 * - Restores focus to the previously focused element when closed
 * - Calls onEscape when Escape key is pressed
 *
 * @returns A ref to attach to the modal container element
 */
export function useFocusTrap<T extends HTMLElement>({
  open,
  onEscape,
}: UseFocusTrapOptions) {
  const containerRef = useRef<T>(null);
  const previousActiveElement = useRef<Element | null>(null);
  // Use ref for callback to avoid re-running effect when callback identity changes
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!open) return;

    // Store the previously focused element to restore later
    previousActiveElement.current = document.activeElement;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onEscapeRef.current?.();
      }

      // Focus trap
      if (e.key === "Tab" && containerRef.current) {
        const focusable = containerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    }

    // Focus the first focusable element
    const firstFocusable =
      containerRef.current?.querySelector<HTMLElement>("button");
    firstFocusable?.focus();

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to previously focused element
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    };
  }, [open]);

  return containerRef;
}
